import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import Skeleton from 'react-loading-skeleton';

import useWindowSize from '@/hooks/useWindowSize';
import Button from '@/ui/Button/Button';
import { ArrowRight } from '@/icons/index';
import Qr from '@/ui/Qr/Qr';
import ContactForm from '@/ui/BusinessContactForm/ContactForm/ContactForm';
import { InformationModal } from '@/ui/index';

import styles from './styles.module.scss';

const DinamicModal = ({ className, ...props }) => {
  const Component = dynamic(() => import('@/ui/Modal/Modal').catch(() => ''), {
    ssr: false,
  });

  return <Component className={className} {...props} />;
};

const Banner = ({
  item,
  type = 'red_banner',
  onClick,
  buttonText,
  buttonHide,
  dangerTitle,
  titlePosition = 'left',
  isBusiness,
}) => {
  const [width] = useWindowSize();
  const [modalActive, setModalActive] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { t } = useTranslation();
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const closeModal = () => {
    setModalActive(false);
  };

  const showSuccessModal = () => {
    closeModal();
    setShowSuccess(true);
  };

  const nameOrTitle = item?.name ?? item?.title;

  const renderTitleComponent = (() => {
    if (dangerTitle) {
      return (
        <h3
          className={classNames([
            styles.title,
            { [styles.left]: titlePosition === 'left' },
          ])}
          dangerouslySetInnerHTML={{ __html: nameOrTitle }}
          itemScope
          itemType='https://schema.org/Title'
        />
      );
    }

    return (
      <h3
        className={classNames([
          styles.title,
          { [styles.center]: titlePosition === 'center' },
        ])}
      >
        {nameOrTitle}
      </h3>
    );
  })();

  return (
    <div
      className={'wrapper section'}
      itemScope
      itemType='https://schema.org/Offer'
    >
      <div
        {...((item?.type === 'image_banner' || type === 'image_banner') &&
          width > 768 && {
            style: {
              backgroundImage: `url("${item?.image}")`,
            },
          })}
        className={classNames([
          styles.banner,
          {
            [styles.red]: type === 'red',
            [styles.redBanner]: type === 'red_banner',
            [styles.white]: type === 'white',
            [styles.black]: type === 'black',
            [styles.dark]: type === 'dark',
            [styles.gray]: type === 'gray_banner',
            [styles.app]: type === 'app',
            [styles.imaged]: type === 'image_banner',
            [styles.isBusiness]: isBusiness,
          },
        ])}
      >
        <div className={styles.content}>
          <div className={styles.top}>
            {item?.icon && (
              <Image
                src={item.icon}
                width={80}
                height={80}
                alt={item?.top_text}
                title={item?.top_text}
              />
            )}
            {item?.top_text && (
              <span className={styles.text}>{item.top_text}</span>
            )}
          </div>
          {width <= 768 && type === 'image_banner' && (
            <div className={styles.imageContainer}>
              <Image
                src={item?.image}
                alt={item?.title}
                title={item?.title}
                loading='lazy'
                layout='fill'
              />
            </div>
          )}
          <p className={styles.title}>{renderTitleComponent}</p>
          {item?.description && (
            <div
              className={styles.text}
              dangerouslySetInnerHTML={{ __html: item.description }}
            />
          )}
          {item?.qrBlock && (
            <div className={styles.qrblock}>
              <Qr data={item.qrBlock} />
            </div>
          )}
          {item?.qr_image && (
            <div className={styles.qrblock}>
              <Qr data={{ qr: item.qr_image, links: item.links }} />
            </div>
          )}
          {!buttonHide && item?.button_text && (
            <Button
              href={item?.button_slug ?? item?.external_link}
              Component={item?.button_slug ? 'router' : 'button'}
              onClick={
                item?.action_type === 'pop-up'
                  ? () => setModalActive(true)
                  : onClick
              }
              className={styles.button}
              data-testid={'iot-services'}
            >
              {buttonText ?? item?.button_text}
              <div className={styles.iconWrapper}>
                <ArrowRight width={20} height={20} />
              </div>
            </Button>
          )}
        </div>

        {(item?.type === 'black_banner' ||
          item?.type === 'gray_banner' ||
          item?.type === 'red_banner' ||
          type === 'white' ||
          type === 'dark' ||
          type === 'app' ||
          type !== 'image_banner') && (
          <div className={styles.imageContainer}>
            {!isImageLoaded && (
              <Skeleton
                className={styles.skeleton}
                containerClassName={styles.skeletonWrapper}
                style={{ position: 'absolute', zIndex: 1 }}
              />
            )}

            <Image
              src={item?.image}
              alt={item?.title}
              title={item?.title}
              loading='lazy'
              layout='fill'
              onLoadingComplete={() => setIsImageLoaded(true)}
              style={{
                visibility: isImageLoaded ? 'visible' : 'hidden',
              }}
            />
          </div>
        )}

        <DinamicModal active={modalActive} setActive={closeModal}>
          <ContactForm showSuccess={showSuccessModal} />
        </DinamicModal>
        <InformationModal
          type={'dark'}
          active={showSuccess}
          onClose={() => setShowSuccess(false)}
          description={t('common:successText')}
        />
      </div>
    </div>
  );
};

export default Banner;
